import { Svg, Rect, Shape } from '@svgdotjs/svg.js';
import { Region, RegionLike } from 'core/geometry';
import { CropHandleCorner, CropHandlePosition } from './crop-handle-corner';
import { pinSVGElement } from './svg-region';

class CropShadow
{
  private background: Rect;
  private shape: Shape;

  constructor(private svgDoc: Svg, private cutOut: Shape)
  {
    this.background = new Rect().fill('white');

    const mask =
        svgDoc
          .mask()
          .add(this.background)
          .add(this.cutOut);

    this.shape =
        this
          .svgDoc
          .rect()
          .addClass('image-cropper-shadow')
          .maskWith(mask);

    this.reconfigure();
  }

  public reconfigure(): void
  {
    const bounds = this.svgDoc.node.getBoundingClientRect();

    this.background.size(bounds.width, bounds.height);
    this.shape.size(bounds.width, bounds.height);
  }
}

function partOf(whole: Region, percentages: Region): Region
{
  return new Region(
      whole.x + percentages.x / 100 * whole.width,
      whole.y + percentages.y / 100 * whole.height,
      percentages.width / 100 * whole.width,
      percentages.height / 100 * whole.height);
}

export class CropHandle
{
  private readonly outline: Rect;
  private readonly corners: CropHandleCorner[];
  private readonly shadow: CropShadow;

  private readonly editableAreaBorder: Rect;
  private readonly cutOutMask: Rect;

  private editableArea: Region = new Region(0, 0, 100, 100);

  constructor(
      private readonly svgDoc: Svg,
      private readonly cutOutShape: Shape)
  {
    this.shadow = new CropShadow(svgDoc, cutOutShape);

    this.outline = svgDoc.rect().addClass('image-cropper-outline');

    this.cutOutMask = svgDoc.rect().fill('white');
    this.cutOutShape.maskWith(this.cutOutMask);

    this.editableAreaBorder = (svgDoc
                               .rect()
                               .addClass('image-cropper-outline-border'));

    this.corners = [
        CropHandlePosition.TopLeft,
        CropHandlePosition.TopRight,
        CropHandlePosition.BottomLeft,
        CropHandlePosition.BottomRight
    ].map((pos) => new CropHandleCorner(this.svgDoc, this.outline, pos));
  }

  public reconfigure(editableArea: RegionLike): void
  {
    this.shadow.reconfigure();

    if (editableArea) {
      this.editableArea = Region.fromSimilar(editableArea);
    }
  }

  public findCorner(node: Element): CropHandleCorner
  {
    return this.corners.find((corner) => corner.shape.node.contains(node));
  }

  public render(selection: Region): void
  {
    const editableRegion = partOf(selection, this.editableArea);

    pinSVGElement(this.outline, selection);
    pinSVGElement(this.cutOutShape, selection);
    pinSVGElement(this.cutOutMask, editableRegion);
    pinSVGElement(this.editableAreaBorder, editableRegion);

    for (const corner of this.corners) {
      corner.update();
    }
  }
}
